"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import {
  DEFAULT_LOCALE,
  getDict,
  isLocale,
  type Dictionary,
  type Locale,
} from "@/locales";
import { LOCALE_COOKIE, LOCALE_COOKIE_MAX_AGE } from "@/libs/locale.constants";
import { useShutter } from "./ShutterController";

/** Read the locale cookie from `document.cookie` (client only). */
const readClientLocale = (): Locale | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]+)`)
  );
  const value = match?.[1];
  return value && isLocale(value) ? value : null;
};

type LocaleContextValue = {
  locale: Locale;
  dict: Dictionary;
  setLocale: (next: Locale) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Provides the current locale + dictionary to the React tree. The server
 * renders with the cookie locale and seeds this provider; the switcher
 * updates the cookie and calls `router.refresh()` so server components
 * re-render with the new locale.
 *
 * The actual shutter overlay is owned by {@link ShutterController} (one
 * level up). Locale changes simply ask the controller to run the cookie
 * + state + refresh side-effects behind a guaranteed full shutter cycle,
 * so the animation always finishes regardless of how fast the server
 * responds.
 */
export default function LocaleProvider({
  initialLocale,
  children,
}: {
  initialLocale: Locale;
  children: React.ReactNode;
}) {
  const router = useRouter();
  const { runShutter } = useShutter();
  const [locale, setLocaleState] = useState<Locale>(
    isLocale(initialLocale) ? initialLocale : DEFAULT_LOCALE
  );

  // Safety net: if a page is statically prerendered (so `initialLocale`
  // is whatever the build-time default was), align with the client-side
  // cookie on mount so the user immediately sees their preferred locale.
  useEffect(() => {
    const cookieLocale = readClientLocale();
    if (!cookieLocale || cookieLocale === locale) return;
    const id = window.setTimeout(() => setLocaleState(cookieLocale), 0);
    return () => window.clearTimeout(id);
    // Only on first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocale = useCallback(
    (next: Locale) => {
      if (!isLocale(next) || next === locale) return;
      runShutter(() => {
        document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; samesite=lax`;
        setLocaleState(next);
        router.refresh();
      });
    },
    [locale, router, runShutter]
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, dict: getDict(locale), setLocale }),
    [locale, setLocale]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used inside <LocaleProvider>");
  }
  return ctx;
};

/** Shorthand — `const t = useDict();` then `t.nav.links.home`. */
export const useDict = (): Dictionary => useLocale().dict;
